import React from "react";
import { Modal } from "react-bootstrap";
import { Exclamation } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

const PreauthorizationIssueModal = ({ show, handleClose, preauth }) => {
  const { viewPreauthorizationInfo } = useSelector(
    (state) => state.preauthorization,
  );

  // === 1. Parse NPHIES Validation Errors from provider_request.valdn_err ===
  const parseValidationErrors = () => {
    const valdn_err = viewPreauthorizationInfo?.provider_request?.valdn_err;
    if (!valdn_err) return [];

    const errors = [];

    const processSection = (data, sectionName) => {
      if (!data || typeof data !== "object") return;

      // Helper: Format message (handle array of strings)
      const formatMsg = (msg, label) => {
        if (Array.isArray(msg)) {
          return (
            <div>
              {label && <span className="fw-bold d-block mb-1">{label}:</span>}
              <ol className="ps-3 mb-0">
                {msg.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ol>
            </div>
          );
        }
        return label ? `${label}: ${msg}` : msg;
      };

      // Case 1: Simple object with .msg (pract, diagnosis, prescrip, etc.)
      if (data.msg) {
        const displayName =
          {
            pract: "Principal Practitioner",
            diag: "Diagnosis",
            diagnosis: "Diagnosis",
            careTeam: "Care Team",
            supportingInfo: "Supporting Info",
            prescrip: "Prescription",
          }[sectionName] ||
          sectionName.charAt(0).toUpperCase() + sectionName.slice(1);

        errors.push({
          type: "validation",
          field: displayName,
          message: formatMsg(data.msg, displayName),
        });
        return;
      }

      // Case 2: Array (like service[])
      if (Array.isArray(data)) {
        let currentLineNo = null;

        data.forEach((item, index) => {
          if (item.no !== undefined) {
            // Save line number
            currentLineNo = item.no;
          }
          if (item.msg) {
            // Found message - use the saved line number or the current index
            const line = currentLineNo !== null ? currentLineNo : index + 1;
            const sectionLabel =
              sectionName === "service"
                ? "Service"
                : sectionName.charAt(0).toUpperCase() + sectionName.slice(1);

            errors.push({
              type: "validation",
              field: null,
              message: formatMsg(item.msg, `In ${sectionLabel} No. ${line}`),
            });
            // Reset line number after using it
            currentLineNo = null;
          }
        });

        return;
      }

      // Case 3: Nested object → recurse
      Object.keys(data).forEach((key) => {
        processSection(data[key], key);
      });
    };

    // Start processing from root
    Object.keys(valdn_err).forEach((key) => {
      processSection(valdn_err[key], key);
    });

    return errors;
  };

  const validationErrors = parseValidationErrors();

  // === 2. Get Payer Response Errors from viewPreauthorizationInfo ===
  const isTag = viewPreauthorizationInfo?.payer_response?.tag;
  const errorRemarkDetails =
    viewPreauthorizationInfo?.payer_response?.remarks || [];
  const disposition = viewPreauthorizationInfo?.payer_response?.disposition;

  const renderRemarkFields = (item = {}) =>
    Object.entries(item)
      .filter(
        ([, value]) => value !== null && value !== undefined && value !== "",
      )
      .map(([field, value]) => {
        const formattedValue =
          typeof value === "object" ? JSON.stringify(value) : String(value);
        return (
          <div key={field} className="text-danger">
            <span className="text-danger fs-11">{field} :</span> <span className="fs-10 text-danger">{formattedValue}</span>
          </div>
        );
      });

  // === Check if any error exists ===
  const hasErrors =
    validationErrors.length > 0 ||
    errorRemarkDetails.length > 0 ||
    !!disposition;

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="text-danger">
        <Modal.Title className="fs-11 d-flex text-danger align-items-center">
          <Exclamation className="me-2 fs-7" />
          Preauthorization Issues - {preauth?.preauthfull_id || "Unknown"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!hasErrors ? (
          <div className="text-muted">No issues found</div>
        ) : (
          <div className="space-y-4">
            {/* NPHIES Validation Errors */}
            {validationErrors.length > 0 && (
              <div>
                <h6 className="text-danger fw-bold border-bottom pb-2 fs-10 mb-3">
                  NPHIES Validation Errors
                </h6>
                <ul className="list-unstyled mb-0">
                  {validationErrors.map((err, idx) => (
                    <li key={idx} className="text-danger mb-2 fs-11">
                      {err.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* PreauthorizationResponse Errors */}
            {(errorRemarkDetails.length > 0 || disposition) && (
              <div>
                <h6 className="text-danger fs-11 fw-bold border-bottom pb-2 mb-3">
                  {isTag ? "NPHIES" : "Payer"} Processing Errors
                </h6>
                <ul className="list-unstyled mb-0">
                  {errorRemarkDetails.map((item, index) => (
                    <li
                      key={`error-${index}`}
                      className="text-danger fs-11 mb-2"
                    >
                      <div className="d-flex align-items-start gap-2">
                        <span className="fw-bold">
                          {isTag ? "(NPHIES)" : "(PAYER)"}
                        </span>
                        <div>
                          <div>
                            {item?.preautherr_code
                              ? `(${item?.preautherr_code}) - ${item?.preautherr_display}`
                              : item?.preautherr_display || "(No display text)"}
                          </div>
                          <div className="mt-1 ps-2">
                            {renderRemarkFields(item)}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                  {disposition && (
                    <li className="text-danger mb-2">
                      <span className="fw-bold fs-11">
                        {isTag ? "(NPHIES)" : "(PAYER)"} Disposition :{" "}
                      </span>
                      <span className="fs-10 text-danger">{disposition}</span>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {viewPreauthorizationInfo?.service_procedures?.map((proc, pIdx) => (
              <React.Fragment key={pIdx}>
                {proc?.adjreason?.map((adj, idx) => (
                  <div className="fw-medium" key={idx}>
                    <span className="fw-medium fs-11 text-danger me-2">
                      {"(Adjudication) :"}
                    </span>{" "}
                    {adj.code ? `[${adj.code}] - ${adj.name}` : adj.name}
                  </div>
                ))}
                {(proc?.reasoncode || proc?.reasondesc) && (
                  <div className="fw-medium">
                    <span className="fw-bold fs-11 text-danger me-2">
                      {"(Adjustication) :"}
                    </span>{" "}
                    <span className="fs-10 text-danger">{proc.reasoncode ? `[${proc.reasoncode}] ` : ""}
                      {proc.reasondesc}</span>
                  </div>
                )}
                {proc?.process_note?.map((note, nIdx) => (
                  <div key={nIdx} className="fw-medium">
                    <span className="fw-bold fs-11 text-danger me-2">{"(Process Note) :"}</span>{" "}
                    <span className="fs-10 text-danger">{note.text}</span>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

PreauthorizationIssueModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  preauth: PropTypes.shape({
    preauthfull_id: PropTypes.string,
  }),
};

export default PreauthorizationIssueModal;
